/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { getImageFile } from 'utils/stringUtils';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { useHistory } from 'react-router-dom';
import ButtonBase from '@material-ui/core/ButtonBase';
import TakeArrow from 'components/NavBarMenu/images/circled-right-arrow.svg';
import ClassNames from 'classnames';
import useContentfulLocale from 'components/useContentfulLocale';
import AssessmentCard from '../AssessmentCard';
import HeroContext from '../../PageContext';
import getServiceType from '../../utils/getServiceType';
import getFormattedType from '../../utils/getFormattedType';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'block',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  cardRoot: {
    boxShadow: 'none !important',
    borderRadius: 4,
    border: `1px solid transparent`,
    textAlign: 'center',
    height: 293,
    position: 'relative',
    backgroundColor: 'transparent',
    zIndex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    color: 'white',
    padding: '14px 16px',
    width: 200,
    [theme.breakpoints.down('800')]: {
      width: '100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    },
    [theme.breakpoints.down('600')]: {
      width: '100%',
    },
  },
  variantE: {
    height: 265,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    [theme.breakpoints.down('xs')]: {
      height: 191,
    },
  },
  time: {
    height: 21,
    width: 55,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    background: '#01619B',
    borderRadius: 2,
    position: 'absolute',
    top: 14,
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  link: {
    ...theme.typography.button,
    display: 'flex',
    alignItems: 'center',
    color: 'white',
    textDecoration: 'none',
    justifyContent: 'flex-start',
    '& img': {
      marginLeft: 5,
    },
  },
  name: {
    display: '-webkit-box',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 87,
    width: '100%',
  },
}));

function FirstCard({ data, clickHandler, variant, locale, otherLanguage }) {
  const classes = useStyles();
  const history = useHistory();
  const { landingInfo } = useContext(HeroContext);
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const imageSrc = getImageFile(data);
  const isVariantE = variant === 'E';
  const {
    slug,
    name,
    title,
    article,
    url,
    subtitle,
    callToAction,
    __typename = '',
  } = data;
  const routeType = getServiceType(data);
  const ctfLocaleFilter = useContentfulLocale();
  const type = getFormattedType(data);
  const isAssessment = type === 'assessment';

  if (isAssessment)
    return (
      <AssessmentCard
        data={data}
        clickHandler={clickHandler}
        imageHeight={isVariantE ? 164 : 189}
        width={200}
        locale={locale || ctfLocaleFilter.locale}
        otherLanguage={otherLanguage}
      />
    );
  const shouldApplyGrayscaleFilter = _get(
    landingInfo,
    'pageText.grayscale',
    false,
  );

  const link = `/${routeType}/${slug}?lang=${locale || ctfLocaleFilter.locale}`;
  const cardRootColor = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#000',
    zIndex: -2,
    borderRadius: 4,
  };
  const cardRootStyle = imageSrc
    ? {
        backgroundImage: `url(${imageSrc})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        position: 'absolute',
        opacity: isAssessment ? 0.2 : 0.5,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        borderRadius: 4,
      }
    : {};

  if (imageSrc && shouldApplyGrayscaleFilter) {
    cardRootStyle.filter = 'grayscale(1)';
  }

  // TODO: make this common once we have a winner
  // eslint-disable-next-line consistent-return
  const redirect = () => {
    if (type === 'source' || type === 'link') {
      if (!url) return alert('Please add a link');
      const isActivity = __typename === 'Activity' || type === 'articles';
      const isInternal =
        url && (url.includes('crediblemind.com') || url.startsWith('/'));
      if (isInternal) {
        const redirectUrl = isActivity
          ? `/articles/${slug}?lang=${locale || ctfLocaleFilter.locale}`
          : url.split('crediblemind.com')[1] || url;
        clickHandler('Client Resource Card Clicked - Home', {
          article: name || title || article,
          type,
          slug,
          resource: redirectUrl,
        });
        history.push(redirectUrl);
      } else {
        clickHandler('Client Resource Card Exited - Home', {
          name: name || title || article,
          type,
          slug,
          exitPage: url,
        });
        window.open(url, '_blank', 'noopener');
      }
    } else {
      clickHandler('Resource Card - Clicked', {
        name: name || title || article,
        type,
        slug,
      });
      history.push(link);
    }
  };

  let header = type.toUpperCase();
  if (type === 'source' && subtitle) {
    header = subtitle;
  }
  let cta =
    !url && type === 'source'
      ? _get(
          siteCopy,
          'pageCopy.clientExclusiveWaitLinkLabel',
          '',
        ).toUpperCase()
      : _get(
          siteCopy,
          'pageCopy.clientExclusiveLearnMoreLabel',
          '',
        ).toUpperCase();
  if (type === 'source' && callToAction && url) {
    cta = callToAction;
  }
  return (
    <ButtonBase disableRipple onClick={redirect} className={classes.container}>
      <div
        className={ClassNames(classes.cardRoot, {
          [classes.variantE]: isVariantE,
        })}
      >
        {imageSrc && <div style={cardRootStyle} />}
        <div style={cardRootColor} />

        <div className={classes.info}>
          <Typography variant="overline" align="left">
            {header}
          </Typography>
          <Typography variant="h5" align="left" className={classes.name}>
            {name || title || article}
          </Typography>
          <ButtonBase className={classes.link}>
            {cta}
            <img src={TakeArrow} alt="" />
          </ButtonBase>
        </div>
      </div>
    </ButtonBase>
  );
}

FirstCard.propTypes = {
  data: PropTypes.object,
  clickHandler: PropTypes.func,
  variant: PropTypes.string,
};

export default FirstCard;
